import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getLocationInfoErrors} from 'molbio/bulk-assembly/assemble-modal/FinalizationUtil';
import {SaveConstructsStep} from 'molbio/bulk-assembly/assemble-modal/SaveConstructsStep';
import {SaveFragmentsAndPrimersStep} from 'molbio/bulk-assembly/assemble-modal/SaveFragmentsAndPrimersStep';
import {FinalizationLocationInfo} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {DEFAULT_SAVE_INFORMATION, LocationInformation} from 'molbio/components/SaveLocationPicker';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';

export enum PerformAssemblyModalStep {
  SAVE_FRAGMENTS_AND_PRIMERS = 'SAVE_FRAGMENTS_AND_PRIMERS',
  SAVE_CONSTRUCTS = 'SAVE_CONSTRUCTS',
}

/**
 * Gets a list of errors for a specified step of the modal.
 * @param stepKey The step to verify.
 * @param constructLocationInfo Information about where to save constructs.
 * @param primerLocationInfo Information about where to save primers.
 * @param fragmentLocationInfo Information about where to save fragments.
 * @param saveFragments Whether to save fragments at all.
 * @param savePrimers Whether to save primers at all.
 */
function getErrorsForStep({
  stepKey,
  constructLocationInfo,
  primerLocationInfo,
  fragmentLocationInfo,
  saveFragments,
  savePrimers,
}: {
  stepKey: PerformAssemblyModalStep;
  constructLocationInfo: LocationInformation;
  primerLocationInfo: LocationInformation;
  fragmentLocationInfo: LocationInformation;
  saveFragments: boolean;
  savePrimers: boolean;
}): Array<string> {
  let errors: Array<string> = [];
  if (stepKey === PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS) {
    errors = [
      ...(saveFragments
        ? getLocationInfoErrors({
            locationInfo: fragmentLocationInfo,
            displayString: 'fragments',
          })
        : []),
      ...(savePrimers
        ? getLocationInfoErrors({locationInfo: primerLocationInfo, displayString: 'primers'})
        : []),
    ];
  } else {
    errors = getLocationInfoErrors({locationInfo: constructLocationInfo, displayString: 'constructs'});
  }

  return errors;
}

/**
 * Get the ModalButtons to display in the modal footer.
 * @param stepKey The current step.
 * @param setStepKey A function to set the current step.
 * @param statusByStep The status for each step.
 * @param setStatusByStep A function to set the status for each step.
 * @param constructLocationInfo Information about where to save the constructs.
 * @param primerLocationInfo Information about where to save primers.
 * @param fragmentLocationInfo Information about where to save fragments.
 * @param saveFragments Whether to save fragments at all.
 * @param savePrimers Whether to save primers at all.
 * @param onClickAssemble A function to be called when the modal is submitted.
 */
function getStepButtons({
  stepKey,
  setStepKey,
  statusByStep,
  setStatusByStep,
  constructLocationInfo,
  fragmentLocationInfo,
  primerLocationInfo,
  saveFragments,
  savePrimers,
  onClickAssemble,
}: {
  stepKey: PerformAssemblyModalStep;
  setStepKey: (step: PerformAssemblyModalStep) => void;
  statusByStep: {
    [key in PerformAssemblyModalStep]: StepItemStatus | null;
  };
  setStatusByStep: (statusByStep: {[key in PerformAssemblyModalStep]: StepItemStatus | null}) => void;
  constructLocationInfo: LocationInformation;
  fragmentLocationInfo: LocationInformation;
  primerLocationInfo: LocationInformation;
  saveFragments: boolean;
  savePrimers: boolean;
  onClickAssemble: () => void;
}): Array<ModalButton> {
  const errors = getErrorsForStep({
    stepKey,
    constructLocationInfo,
    primerLocationInfo,
    fragmentLocationInfo,
    saveFragments,
    savePrimers,
  });
  const disableNextButton = errors.length > 0;
  const assembleButton = {
    text: 'Assemble',
    submit: true,
    disabled: disableNextButton,
    ...(disableNextButton ? {tooltipTitle: errors.join(' ')} : {}),
    onClick: onClickAssemble,
  };
  if (stepKey === PerformAssemblyModalStep.SAVE_CONSTRUCTS) {
    return [
      {
        text: 'Back',
        onClick: () => {
          setStepKey(PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS);
          setStatusByStep({
            ...statusByStep,
            [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: StepItemStatus.UNVISITED,
          });
        },
      },
      assembleButton,
    ];
  } else {
    return [
      {text: 'Cancel', cancel: true},
      {
        text: 'Next',
        submit: true,
        disabled: disableNextButton,
        tooltipTitle: disableNextButton ? errors.join(' ') : undefined,
        onClick: () => {
          if (statusByStep.SAVE_FRAGMENTS_AND_PRIMERS !== StepItemStatus.ERROR) {
            setStatusByStep({
              ...statusByStep,
              [PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS]: StepItemStatus.DONE,
            });
          }
          setStepKey(PerformAssemblyModalStep.SAVE_CONSTRUCTS);
        },
        dataTestAttrs: {component: 'PerformAssemblyModal', key: ModalButtonKey.NEXT},
      },
    ];
  }
}

export interface PerformAssemblyModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (saveInformation: FinalizationLocationInfo) => void;
  currentUserId: string;
  canCreatePrimers: boolean;
  numConstructs: number;
}

/**
 * A modal used to select the location(s) in which the assembly outputs should be saved. Upon submission, the
 * assembly is completed on the backend.
 *
 * @param {PerformAssemblyModalProps} props Component props
 * @param {Function} props.onRequestClose Handler for closing the modal without submitting
 * @param {Function} props.onRequestSubmit Handler for submitting the modal
 * @param {string} props.currentUserId The API identifier of the current user, which is used for displaying
 *                                     the create worklist UI
 * @param {boolean} props.canCreatePrimers Whether or not the assembly may generate primers
 */
export default function PerformAssemblyModal(props: PerformAssemblyModalProps): JSX.Element {
  const initialSaveInformation = {
    ...DEFAULT_SAVE_INFORMATION,
    targetFolderId: props.initialFolderId ?? undefined,
    tagSchemaId: null,
  };
  const [saveFragments, setSaveFragments] = useState<boolean>(false);
  const [fragmentLocationInfo, setFragmentLocationInfo] =
    useState<LocationInformation>(initialSaveInformation);
  const [constructLocationInfo, setConstructLocationInfo] =
    useState<LocationInformation>(initialSaveInformation);
  const [savePrimers, setSavePrimers] = useState<boolean>(true);
  const [primerLocationInfo, setPrimerLocationInfo] = useState<LocationInformation>(initialSaveInformation);
  const [statusByStep, setStatusByStep] = useState<{
    [key in PerformAssemblyModalStep]: StepItemStatus | null;
  }>({
    [PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS]: StepItemStatus.UNVISITED,
    [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: StepItemStatus.UNVISITED,
  });
  const initialStep = PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS;
  const [stepKey, setStepKey] = useState<PerformAssemblyModalStep>(initialStep);

  useEffect(() => {
    const currentStatus = statusByStep[stepKey];
    const errors = getErrorsForStep({
      stepKey,
      constructLocationInfo,
      fragmentLocationInfo,
      primerLocationInfo,
      saveFragments,
      savePrimers,
    });
    const newStatus = errors.length > 0 ? StepItemStatus.ERROR : StepItemStatus.UNVISITED;
    if (currentStatus !== newStatus) {
      setStatusByStep({
        ...statusByStep,
        [stepKey]: newStatus,
      });
    }
  }, [
    stepKey,
    statusByStep,
    setStatusByStep,
    saveFragments,
    savePrimers,
    constructLocationInfo,
    fragmentLocationInfo,
    primerLocationInfo,
  ]);

  const onClickAssemble = (): void => {
    assert(
      (fragmentLocationInfo.targetFolderId != null || !saveFragments) &&
        constructLocationInfo.targetFolderId != null &&
        (primerLocationInfo.targetFolderId != null || !savePrimers),
      'Expected fragment, construct, and primer folders to be specified when saving those entities'
    );
    props.onRequestSubmit({
      fragmentSaveInfo: saveFragments
        ? {
            folderId: fragmentLocationInfo.targetFolderId!,
            tagSchemaId: fragmentLocationInfo.tagSchemaId,
            worklistId: fragmentLocationInfo.worklistParams?.targetWorklistId ?? null,
            newWorklistName: fragmentLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
          }
        : null,
      constructSaveInfo: {
        folderId: constructLocationInfo.targetFolderId,
        tagSchemaId: constructLocationInfo.tagSchemaId,
        worklistId: constructLocationInfo.worklistParams?.targetWorklistId ?? null,
        newWorklistName: constructLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
      },
      primerSaveInfo: savePrimers
        ? {
            folderId: primerLocationInfo.targetFolderId!,
            tagSchemaId: primerLocationInfo.tagSchemaId,
            worklistId: primerLocationInfo.worklistParams?.targetWorklistId ?? null,
            newWorklistName: primerLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
          }
        : null,
    });
  };

  const buttons = getStepButtons({
    stepKey,
    setStepKey,
    statusByStep,
    setStatusByStep,
    constructLocationInfo,
    fragmentLocationInfo,
    primerLocationInfo,
    saveFragments,
    savePrimers,
    onClickAssemble,
  });

  const sharedWorklistParams = {
    newWorklistOwnerId: props.currentUserId,
    shouldShowOverflowWarningForNewWorklist:
      props.numConstructs > BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST,
    shouldShowOverflowWarningForExistingWorklist: props.numConstructs > 100,
  };

  return (
    <Modal title="Assemble DNA" buttons={buttons} onRequestClose={props.onRequestClose}>
      <StepNav<PerformAssemblyModalStep>
        steps={[
          {
            key: PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS,
            title: props.canCreatePrimers ? 'Save fragments and primers' : 'Save fragments',
          },
          {key: PerformAssemblyModalStep.SAVE_CONSTRUCTS, title: 'Save constructs'},
        ]}
        statusByStep={statusByStep}
        currentStepKey={stepKey}
        onUpdateCurrentStep={setStepKey}
        variant={StepNavVariant.FLAT}
      />
      {stepKey === PerformAssemblyModalStep.SAVE_FRAGMENTS_AND_PRIMERS && (
        <SaveFragmentsAndPrimersStep
          {...sharedWorklistParams}
          fragmentLocationInfo={fragmentLocationInfo}
          onUpdateFragmentLocationInfo={setFragmentLocationInfo}
          primerLocationInfo={primerLocationInfo}
          onUpdatePrimerLocationInfo={setPrimerLocationInfo}
          shouldSaveFragments={saveFragments}
          setShouldSaveFragments={setSaveFragments}
          shouldSavePrimers={savePrimers}
          setShouldSavePrimers={setSavePrimers}
          canSavePrimers={props.canCreatePrimers}
        />
      )}
      {stepKey === PerformAssemblyModalStep.SAVE_CONSTRUCTS && (
        <SaveConstructsStep
          locationInfo={constructLocationInfo}
          onUpdateLocationInfo={setConstructLocationInfo}
          worklistParams={{
            ...sharedWorklistParams,
            useWorklistCheckboxLabel: 'Add constructs to a worklist',
            pluralEntityLabel: 'constructs',
          }}
          stepDescription="Add constructs to a folder and optionally set a schema"
          isRequiredParams={{isRequired: true}}
          oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
        />
      )}
    </Modal>
  );
}
